<template>
  <div class="import-tab">
    <WaveformTableBase
      ref="waveformTable"
      table-ref="importTable"
      :label-id="labelId"
      :dict-item-id="dictItemId"
      :search-config="searchConfig"
      :table-columns="tableColumns"
      :table-operations="tableOperations"
      batch-type="import"
      @load-data="handleLoadData"
      @view-wave="handleViewWave"
      @download="handleDownload"
      @batch-migrate="handleBatchMigrate"
      @migrate="handleMigrate"
      @wave-close="handleWaveClose">
    </WaveformTableBase>
  </div>
</template>

<script>
import { waveformNpyResolveList, waveformNpyResolveView, waveformNpyObsDownloadStream } from "@/api/waveform/manage";
import WaveformTableBase from '@/components/waveform/WaveformTableBase.vue'

export default {
  name: 'ImportTab',
  components: {
    WaveformTableBase
  },
  props: {
    labelId: {
      type: [String, Number, null],
      default: null
    },
    dictItemId: {
      type: [String, Number, null],
      default: null
    }
  },
  data() {
    return {
      // 搜索配置
      searchConfig: [
        {
          label: '名称',
          prop: 'name',
          type: 'input',
          placeholder: '请输入名称',
          width: '160px'
        },
        {
          label: '状态',
          prop: 'processingStatus',
          type: 'select',
          placeholder: '请选择状态',
          width: '160px',
          options: [
            { label: '全部', value: '' },
            { label: '未处理', value: 1 },
            { label: '处理完成', value: 2 },
            { label: '处理失败', value: 3 }
          ]
        },
        {
          label: '时间范围',
          prop: 'createTime',
          type: 'datetimerange',
          width: '350px',
          format: 'yyyy-MM-dd HH:mm:ss',
          valueFormat: 'yyyy-MM-dd HH:mm:ss'
        }
      ],
      // 表格列配置
      tableColumns: [
        {
          prop: 'name',
          label: '名称',
          align: 'center',
          width: '300px'
        },
        {
          prop: 'processingStatus',
          label: '状态',
          align: 'center',
          isStatusColumn: true,
          width: '200px'
        },
        {
          prop: 'createTime',
          label: '创建时间',
          align: 'center'
        }
      ],
      // 表格操作按钮配置
      tableOperations: [
        {
          label: '查看波形',
          type: 'text',
          size: 'mini',
          handler: 'handleView'
        },
        {
          label: '迁移至',
          type: 'text',
          size: 'mini',
          handler: 'handleMigrateVisible'
        }
      ]
    }
  },
  methods: {
    // 处理加载数据事件
    async handleLoadData(params, callback) {
      if (!this.labelId) {
        // 如果labelId为空，清空数据
        callback([], 0);
        return;
      }
      
      try {
        // 构建基础请求参数
        const requestParams = {
          page: params.pageData.currentPage,
          size: params.pageData.pageSize,
          waveformTagId: params.labelId,  // 标签的id
          dictItemId: params.dictItemId   //  左侧菜单栏的id
        };
        
        // 根据搜索配置自动构建参数
        this.searchConfig.forEach(config => {
          const value = params.searchForm[config.prop];
          if (value !== null && value !== undefined && value !== '') {
            if (config.type === 'datetimerange' && Array.isArray(value) && value.length === 2) {
              // 时间范围特殊处理
              requestParams.startTime = value[0];
              requestParams.endTime = value[1];
            } else if (config.type !== 'datetimerange') {
              // 其他类型直接赋值
              requestParams[config.prop] = value;
            }
          }
        });
        
        const res = await waveformNpyResolveList(requestParams);
        
        if (res.code === 200) {
          callback(res.data.records || [], res.data.total || 0);
        } else {
          this.$message({
            message: res.message || '获取数据失败',
            type: 'error'
          });
          callback([], 0);
        }
      } catch (error) {
        console.error('加载数据失败:', error);
        this.$message({
          message: '网络错误，获取数据失败',
          type: 'error'
        });
        callback([], 0);
      }
    },

    // 处理查看波形事件
    async handleViewWave(id, callback) {
      try {
        const res = await waveformNpyResolveView(id);
        if (res.code === 200 && res.data) {
          callback(res.data);
        } else {
          callback(null);
        }
      } catch (error) {
        console.error('查看波形失败:', error);
        callback(null);
      }
    },

    // 处理下载事件
    async handleDownload(row) {
      if (!row.obsPath) {
        this.$message({
          message: '文件路径不存在',
          type: 'warning'
        });
        return;
      }

      // 显示下载中的加载动画
      const loadingInstance = this.$loading({
        lock: true,
        text: '下载中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        // 使用文件流下载
        const response = await waveformNpyObsDownloadStream({ obsUrl: row.obsPath, id: row.id });
        
        // 检查响应数据是否为Blob类型
        if (!(response.data instanceof Blob)) {
          throw new Error('下载失败：响应数据不是文件流');
        }
        
        // 直接使用响应的Blob数据
        const blob = response.data;
        
        // 创建下载链接
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = row.name || 'download.npy';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // 释放内存
        window.URL.revokeObjectURL(link.href);
        
        // 关闭加载动画
        loadingInstance.close();
        
        this.$message({
          message: '下载成功',
          type: 'success'
        });
      } catch (error) {
        // 关闭加载动画
        loadingInstance.close();
        
        console.error('下载失败:', error);
        this.$message({
          message: '下载失败，请重试',
          type: 'error'
        });
      }
    },

    // 处理批量迁移事件
    handleBatchMigrate(recordIds, batchType) {
      this.$emit('batch-migrate', recordIds, batchType);
    },

    // 处理迁移事件
    handleMigrate(recordIds, batchType) {
      this.$emit('migrate', recordIds, batchType);
    },

    // 处理波形弹窗关闭事件
    handleWaveClose() {
      // 可以在这里添加关闭后的清理逻辑
    },

    // 代理方法：加载数据
    loadData() {
      if (this.$refs.waveformTable) {
        this.$refs.waveformTable.loadData();
      }
    }
  }
}
</script>

<style scoped>
.import-tab {
  padding: 0;
}
</style>